import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, MenuItem, CircularProgress, Alert, Chip,
  Stack, Breadcrumbs, Link, Avatar, IconButton, Drawer, Tooltip
} from '@mui/material';
import { 
  Assessment, Refresh, 
  CheckCircle, Cancel, History, WatchLater, PersonOff,
  Download as DownloadIcon, PeopleAlt
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../config/api';
import { format } from 'date-fns';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  PRESENT: { label: 'Present', color: '#38A169', bg: '#F0FFF4', icon: <CheckCircle fontSize="small" /> },
  ABSENT: { label: 'Absent', color: '#E53E3E', bg: '#FFF5F5', icon: <Cancel fontSize="small" /> },
  LATE: { label: 'Late', color: '#D69E2E', bg: '#FFFFF0', icon: <WatchLater fontSize="small" /> },
  EXCUSED: { label: 'Excused', color: '#4C51BF', bg: '#EBF4FF', icon: <History fontSize="small" /> },
};

const AttendancePage = () => {
  const navigate = useNavigate();
  const [classGroups, setClassGroups] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [attendance, setAttendance] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [studentHistory, setStudentHistory] = useState<any>(null);

  const [filter, setFilter] = useState({
    classGroupId: '',
    date: new Date().toISOString().split('T')[0]
  });

  const getHeaders = useCallback(() => {
    const userInfo = localStorage.getItem('userInfo');
    const token = userInfo ? JSON.parse(userInfo).token : null;
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchClassGroups = useCallback(async () => {
    try {
      // /groups/my returns role-filtered groups:
      // TEACHER → their assigned classes only
      // ADMIN/PRINCIPAL → all tenant groups
      const res = await axios.get(getApiUrl('groups/my'), { headers: getHeaders() });
      const groups = res.data || [];
      setClassGroups(groups);
      if (groups.length > 0 && !filter.classGroupId) {
        setFilter(f => ({ ...f, classGroupId: groups[0].id }));
      }
    } catch (err: any) {
      console.error('Error fetching class groups:', err);
    }
  }, [getHeaders, filter.classGroupId]);

  const fetchAttendance = useCallback(async () => {
    if (!filter.classGroupId) return;
    try {
      setLoading(true);
      setError('');
      
      // Fetch attendance records AND the group's student list in parallel
      // /groups/:id returns { students: [...], teacher: {...}, ... }
      const [attRes, groupRes] = await Promise.all([
        axios.get(getApiUrl(`attendance-records?classGroupId=${filter.classGroupId}&date=${filter.date}`), { headers: getHeaders() }),
        axios.get(getApiUrl(`groups/${filter.classGroupId}`), { headers: getHeaders() }),
      ]);
      
      setAttendance(attRes.data || []);
      setStudents(groupRes.data?.students || []);
    } catch (err: any) {
      console.error('Error loading attendance:', err);
      setError(err.response?.data?.message || 'Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  }, [filter.classGroupId, filter.date, getHeaders]);

  useEffect(() => { fetchClassGroups(); }, [fetchClassGroups]);
  useEffect(() => { fetchAttendance(); }, [fetchAttendance]);

  const handleMarkAttendance = async (studentId: string, status: string) => {
    try {
      await axios.post(getApiUrl('attendance-records'), {
        classGroupId: filter.classGroupId,
        date: filter.date,
        records: [{ studentId, status, notes: '' }]
      }, { headers: getHeaders() });

      setAttendance(prev => {
        const filtered = prev.filter(a => a.studentId !== studentId);
        return [...filtered, { studentId, status }];
      });
      setSuccessMsg('Attendance marked');
      setTimeout(() => setSuccessMsg(''), 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to mark attendance');
    }
  };

  const openStudentDrawer = async (student: any) => {
    setSelectedStudent(student);
    setDrawerOpen(true);
    setStudentHistory(null);
    try {
      setStatsLoading(true);
      const res = await axios.get(getApiUrl(`attendance-records/student/${student.id}`), { headers: getHeaders() });
      setStudentHistory(res.data);
    } catch (err) {
      console.error('Error fetching student history:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const getStatus = (studentId: string) => {
    const record = attendance.find(a => a.studentId === studentId);
    return record?.status || null;
  };

  const handleMarkAll = async (status: string) => {
    try {
      setLoading(true);
      const records = students.map(s => ({ studentId: s.id, status, notes: '' }));
      await axios.post(getApiUrl('attendance-records'), {
        classGroupId: filter.classGroupId,
        date: filter.date,
        records
      }, { headers: getHeaders() });
      await fetchAttendance();
      setSuccessMsg(`All students marked as ${status}`);
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Bulk mark failed');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    const rows = students.map(s => {
      const status = getStatus(s.id) || 'NOT MARKED';
      return `${s.firstName} ${s.lastName},${s.studentId},${status}`;
    });
    const csv = ['Name,Student ID,Status', ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_${filter.date}.csv`;
    a.click();
  };

  // Computed stats
  const stats = {
    present: attendance.filter(a => a.status === 'PRESENT').length,
    absent: attendance.filter(a => a.status === 'ABSENT').length,
    late: attendance.filter(a => a.status === 'LATE').length,
    excused: attendance.filter(a => a.status === 'EXCUSED').length,
    unmarked: students.length - attendance.length,
  };

  const chartData = [
    { name: 'Present', value: stats.present, color: '#38A169' },
    { name: 'Absent', value: stats.absent, color: '#E53E3E' },
    { name: 'Late', value: stats.late, color: '#D69E2E' },
    { name: 'Excused', value: stats.excused, color: '#4C51BF' },
    { name: 'Unmarked', value: stats.unmarked, color: '#CBD5E0' },
  ].filter(d => d.value > 0);

  const attendanceRate = students.length > 0 ? Math.round((stats.present / students.length) * 100) : 0;
  const selectedGroup = classGroups.find(g => g.id === filter.classGroupId);

  return (
    <Box sx={{ p: 4, bgcolor: '#F7FAFC', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs sx={{ mb: 1, fontSize: '0.85rem' }}>
          <Link underline="hover" color="inherit" onClick={() => navigate('/dashboard')} sx={{ cursor: 'pointer' }}>Dashboard</Link>
          <Link underline="hover" color="inherit" onClick={() => navigate('/dashboard/academics')} sx={{ cursor: 'pointer' }}>Academics</Link>
          <Typography color="text.primary">Attendance</Typography>
        </Breadcrumbs>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h3" fontWeight={800} sx={{ color: '#263B7C', letterSpacing: '-0.02em', fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
              Attendance Control
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              Daily presence management and student attendance visibility.
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportCSV}
              sx={{ borderRadius: '12px', px: 3, fontWeight: 700, borderColor: '#E2E8F0', color: '#4A5568', bgcolor: 'white' }}>
              Export CSV
            </Button>
            <Button variant="contained" startIcon={<CheckCircle />} onClick={() => handleMarkAll('PRESENT')}
              sx={{ bgcolor: '#38A169', borderRadius: '12px', px: 3, fontWeight: 700, '&:hover': { bgcolor: '#2F855A' } }}>
              Mark All Present
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, borderRadius: '20px', border: '1px solid #edf2f7', mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="flex-end">
          <TextField
            select label="Class Group" size="small"
            value={filter.classGroupId}
            onChange={e => setFilter(f => ({ ...f, classGroupId: e.target.value }))}
            sx={{ minWidth: 220, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          >
            {classGroups.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
          </TextField>
          <TextField
            label="Date" type="date" size="small"
            InputLabelProps={{ shrink: true }}
            value={filter.date}
            onChange={e => setFilter(f => ({ ...f, date: e.target.value }))}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchAttendance}
            sx={{ borderRadius: '12px', fontWeight: 700, borderColor: '#E2E8F0', color: '#4A5568' }}>
            Refresh
          </Button>
        </Stack>
      </Paper>

      {/* Analytics */}
      {!loading && students.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 4, mb: 4 }}>
          {/* Stat cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 3 }}>
            {[
              { label: 'Present', value: stats.present, color: '#38A169', bg: '#F0FFF4' },
              { label: 'Absent', value: stats.absent, color: '#E53E3E', bg: '#FFF5F5' },
              { label: 'Late', value: stats.late, color: '#D69E2E', bg: '#FFFFF0' },
              { label: 'Attendance Rate', value: `${attendanceRate}%`, color: '#4C51BF', bg: '#EBF4FF' },
            ].map((stat, i) => (
              <Paper key={i} sx={{ p: 3, borderRadius: '20px', border: '1px solid #edf2f7', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', bgcolor: stat.bg }}>
                <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase">{stat.label}</Typography>
                <Typography variant="h3" fontWeight={800} color={stat.color} sx={{ mt: 1 }}>{stat.value}</Typography>
              </Paper>
            ))}
          </Box>

          {/* Pie Chart */}
          <Paper sx={{ p: 3, borderRadius: '20px', border: '1px solid #edf2f7', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
            <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase">Distribution</Typography>
            <Box sx={{ height: 140 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={chartData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" paddingAngle={3}>
                    {chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                  </Pie>
                  <RechartsTooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', fontSize: 12 }} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, fontWeight: 700 }} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Messages */}
      {successMsg && <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }}>{successMsg}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setError('')}>{error}</Alert>}

      {/* Attendance Table */}
      <Paper sx={{ borderRadius: '24px', border: '1px solid #edf2f7', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
        <Box sx={{ p: 2.5, borderBottom: '1px solid #edf2f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: '#EBF4FF', color: '#4C51BF', width: 36, height: 36, borderRadius: '10px' }}>
              <PeopleAlt sx={{ fontSize: 18 }} />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={800} color="#2D3748">
                {selectedGroup?.name || 'Class Group'} — {format(new Date(filter.date + 'T12:00:00'), 'EEEE, MMM do yyyy')}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>{students.length} students enrolled</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => handleMarkAll('ABSENT')}
              sx={{ fontWeight: 700, color: '#E53E3E', bgcolor: '#FFF5F5', borderRadius: '8px', px: 2, fontSize: '0.75rem' }}>
              All Absent
            </Button>
          </Stack>
        </Box>

        {loading ? (
          <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress /></Box>
        ) : students.length === 0 ? (
          <Box sx={{ py: 10, textAlign: 'center', opacity: 0.5 }}>
            <PersonOff sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="body1" fontWeight={600}>No students found</Typography>
            <Typography variant="body2">Please select a class group or check your records.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: '#F8F9FA' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800, color: '#4A5568', textTransform: 'uppercase', fontSize: '0.75rem' }}>Student</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: '#4A5568', textTransform: 'uppercase', fontSize: '0.75rem' }}>ID</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 800, color: '#4A5568', textTransform: 'uppercase', fontSize: '0.75rem' }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 800, color: '#4A5568', textTransform: 'uppercase', fontSize: '0.75rem' }}>Quick Mark</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 800, color: '#4A5568', textTransform: 'uppercase', fontSize: '0.75rem' }}>History</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {students.map((student) => {
                  const status = getStatus(student.id);
                  const cfg = status ? STATUS_CONFIG[status] : null;
                  return (
                    <TableRow key={student.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar sx={{ bgcolor: cfg ? `${cfg.color}20` : '#F7FAFC', color: cfg?.color || '#A0AEC0', width: 38, height: 38, borderRadius: '10px', fontSize: '0.9rem', fontWeight: 800 }}>
                            {student.firstName?.[0]}{student.lastName?.[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={800} color="#2D3748">{student.firstName} {student.lastName}</Typography>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>{student.grade || ''}</Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700} color="#4A5568">{student.studentId}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        {cfg ? (
                          <Chip label={cfg.label} size="small"
                            sx={{ fontWeight: 800, color: cfg.color, bgcolor: cfg.bg, borderRadius: '8px', fontSize: '0.7rem', px: 0.5 }} />
                        ) : (
                          <Chip label="Not Marked" size="small" sx={{ fontWeight: 700, color: '#A0AEC0', bgcolor: '#F7FAFC', borderRadius: '8px', fontSize: '0.7rem' }} />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                            <Tooltip title={`Mark ${val.label}`} key={key}>
                              <IconButton
                                size="small"
                                onClick={() => handleMarkAttendance(student.id, key)}
                                sx={{
                                  color: status === key ? val.color : '#CBD5E0',
                                  bgcolor: status === key ? val.bg : 'transparent',
                                  borderRadius: '8px',
                                  border: status === key ? `1px solid ${val.color}40` : '1px solid transparent',
                                  transition: 'all 0.2s',
                                  '&:hover': { color: val.color, bgcolor: val.bg }
                                }}
                              >
                                {val.icon}
                              </IconButton>
                            </Tooltip>
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View attendance history">
                          <IconButton size="small" sx={{ color: '#4C51BF', bgcolor: '#EBF4FF', borderRadius: '8px', '&:hover': { bgcolor: '#C3DAFE' } }}
                            onClick={() => openStudentDrawer(student)}>
                            <Assessment fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Student Attendance History Drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, bgcolor: '#F7FAFC' } }}>
        {selectedStudent && (
          <Box>
            <Box sx={{ p: 4, bgcolor: 'white', borderBottom: '1px solid #edf2f7' }}>
              <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <Avatar sx={{ bgcolor: '#EBF4FF', color: '#4C51BF', width: 56, height: 56, borderRadius: '16px', fontWeight: 800, fontSize: '1.1rem' }}>
                  {selectedStudent.firstName?.[0]}{selectedStudent.lastName?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={800} color="#2D3748">{selectedStudent.firstName} {selectedStudent.lastName}</Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>{selectedStudent.studentId} • {selectedStudent.grade}</Typography>
                </Box>
              </Stack>
            </Box>

            <Box sx={{ p: 4 }}>
              {statsLoading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
              ) : studentHistory ? (
                <>
                  <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase">Attendance Summary</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2, mb: 4 }}>
                    {[
                      { label: 'Present', value: studentHistory.summary.present, color: '#38A169', bg: '#F0FFF4' },
                      { label: 'Absent', value: studentHistory.summary.absent, color: '#E53E3E', bg: '#FFF5F5' },
                      { label: 'Late', value: studentHistory.summary.late, color: '#D69E2E', bg: '#FFFFF0' },
                      { label: 'Rate', value: `${studentHistory.summary.attendanceRate}%`, color: '#4C51BF', bg: '#EBF4FF' },
                    ].map((s, i) => (
                      <Paper key={i} sx={{ p: 2, borderRadius: '16px', border: '1px solid #edf2f7', boxShadow: 'none', bgcolor: s.bg }}>
                        <Typography variant="caption" fontWeight={800} color="text.secondary">{s.label}</Typography>
                        <Typography variant="h5" fontWeight={800} color={s.color}>{s.value}</Typography>
                      </Paper>
                    ))}
                  </Box>

                  <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase">Recent History</Typography>
                  <Stack spacing={1.5} sx={{ mt: 2 }}>
                    {studentHistory.records.slice(0, 12).map((rec: any, i: number) => {
                      const cfg = STATUS_CONFIG[rec.status] || STATUS_CONFIG['PRESENT'];
                      return (
                        <Paper key={i} sx={{ p: 2, borderRadius: '12px', border: '1px solid #edf2f7', boxShadow: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box sx={{ color: cfg.color }}>{cfg.icon}</Box>
                            <Typography variant="body2" fontWeight={700}>{format(new Date(rec.date), 'EEE, MMM do')}</Typography>
                          </Stack>
                          <Chip label={cfg.label} size="small" sx={{ fontWeight: 800, color: cfg.color, bgcolor: cfg.bg, borderRadius: '6px', fontSize: '0.65rem' }} />
                        </Paper>
                      );
                    })}
                    {studentHistory.records.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No historical records found</Typography>
                    )}
                  </Stack>
                </>
              ) : null}
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default AttendancePage;
